import { contentful } from '@neurotrack/web-core'
import { getCookie } from '@neurotrack/web-core/src/util/cookies'
import { getStartingLocale } from '@neurotrack/web-core/src/util/env'
import { ActionContext } from 'vuex'
import { IRootStore } from '../models/Store'

const iconCognitiveScience = require('../assets/svg/icon-cognitives-science.png')
const iconHabitScience = require('../assets/svg/icon-building-habits.png')
const iconNutrition = require('../assets/svg/icon-nutrition.png')
const iconExercise = require('../assets/svg/icon-exercise.png')
const iconSleep = require('../assets/svg/icon-sleep.png')
const iconSocial = require('../assets/svg/icon-social-engagement.png')
const iconStress = require('../assets/svg/icon-stress-management.png')
const iconMotivation = require('../assets/svg/icon-motivation.svg')
const iconCognitiveTraining = require('../assets/svg/icon-cognitive-training.png')

const SET_LIBRARY = 'SET_LIBRARY'
const SET_FAQS = 'SET_FAQS'
const SET_CATEGORIES = 'SET_CATEGORIES'

const categoryImages: { [key: string]: string } = {
  COGNITIVE_SCIENCE: iconCognitiveScience,
  HABIT_SCIENCE: iconHabitScience,
  NUTRITION: iconNutrition,
  EXERCISE: iconExercise,
  SLEEP: iconSleep,
  SOCIAL: iconSocial,
  STRESS: iconStress,
  MOTIVATION: iconMotivation,
  COGNITIVE_TRAINING: iconCognitiveTraining
}

export interface  ILibrary {
  id: string,
  url: string,
  title: string,
  language: string,
  category: string,
  category_index: number,
  category_formatted: string,
  image_url: string,
  description: string
}

export interface ILearnState {
  library: ILibrary[]
}

export interface ICategory {
  key: string,
  text: string,
  img: string
}

export interface IFAQsCategoryList {
  [key: string]: string
}

export interface IFAQsState {
  faqs: Map<string, string>,
  categories: IFAQsCategoryList
}

type Context = ActionContext<ILearnState, IRootStore>
type FAQContext = ActionContext<IFAQsState, any>
type Entry = typeof contentful.ContentEntry

export default {
  namespaced: true,
  state: () => {},
  modules: {
    learn: {
      namespaced: true,
      state: (): ILearnState  => ({
        library: []
      }),
      mutations: {
        [SET_LIBRARY](state: ILearnState, library: ILibrary[]) {
          state.library = library
        }
      },
      getters: {
        getCategories: ({ library }: { library: ILibrary[] }): ICategory[] => {
          const tempMap: Map<string, string> = new Map<string, string>()
          library.map((item: ILibrary) => tempMap.set(item.category, item.category_formatted))
          const cats: ICategory[] = []
          tempMap.forEach((v: string, k: string) => {
            cats.push({
              key: k, text: v, img: categoryImages[k]
            })
          })
          return cats
        },
        getCategoryItems: (
          { library }: { library: ILibrary[] }
        ) => (category: string) => library.filter((item: ILibrary) => item.category === category),
        getCategoryText: (state: any, getters: any) => (key: string) => {
          const cat = getters.getCategories.filter((c: ICategory) => c.key === key.toUpperCase())
          return cat ? cat[0].text : ''
        }
      },
      actions: {
        async loadLibraryData({ state, commit }: Context) {
          if (state.library.length === 0) {
            const url = new URL(getCookie('content_root') || '')
            const lang = getStartingLocale()
            const res = await fetch(`${url.protocol}//${url.host}/${lang}/library.json`)
            const data = await res.json()
            commit(SET_LIBRARY, data.items)
          }
        }
      }
    },
    faqs: {
      namespaced: true,
      state: (): IFAQsState => ({
        faqs: new Map(),
        categories: {}
      }),
      getters: {
        faqs: ({ faqs }: IFAQsState) => (category: string) => (
          category ? faqs.get(category) : faqs
        ),
        categories: ({ categories }: IFAQsState) => categories
      },
      actions: {
        setFAQs({ commit }: FAQContext, faqs: Map<string, string>) {
          commit(SET_FAQS, faqs)
        },
        async loadFromContentful({ commit, dispatch, rootState }: FAQContext) {
          const client = contentful.createClient()
          const locale = getStartingLocale() === 'en' ? 'en-US' : 'ja'
          const entries = await client.getEntries({
            content_type: 'faq',
            locale,
            'fields.usage[in]': 'website',
            order: '-sys.createdAt'
          })

          /** Create a map of categories to FAQ questions */
          const entryMap: Map<string, Entry[]> = new Map()
          const categories: IFAQsCategoryList = {}

          entries.items.forEach((entry: Entry) => {
            const { name } = entry.fields.category.fields
            const { slug } = entry.fields.category.fields

            // Ignore MyLinkX items for non-Sompo users
            const sompoOrgs = ['sompo', 'sompo_preview_2019', 'japan_01']
            if (
              slug === 'mylinkx'
              && !sompoOrgs.includes(rootState.user.profile.organization)
            ) { return }

            const entryList = entryMap.get(slug)
            if (entryList) {
              entryList.push(entry)
            } else {
              entryMap.set(slug, [entry])
            }
            categories[slug] = name
          })
          dispatch('setFAQs', entryMap)
          commit(SET_CATEGORIES, categories)
        }
      },
      mutations: {
        [SET_FAQS](state: IFAQsState, faqs: Map<string, string>) {
          state.faqs = faqs
        },
        [SET_CATEGORIES](state: IFAQsState, categories: IFAQsCategoryList) {
          state.categories = categories
        }
      }
    }
  }
}
